import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Globe, 
  Zap, 
  Users, 
  MessageSquare, 
  BarChart3, 
  Settings,
  ArrowRight,
  Sparkles,
  LogOut,
  CheckCircle,
  Plus,
  Building2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Button } from '../components/Button';
import { Input } from '../components/Input';

interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface Website {
  id: string;
  name: string;
  slug: string;
  updated_at: string;
  organizations?: { name: string };
}

export function DashboardPage() {
  const { user, profile, signOut } = useAuth();
  const { request } = useApi();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [websites, setWebsites] = useState<Website[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewWebsiteModal, setShowNewWebsiteModal] = useState(false);
  const [showNewOrgModal, setShowNewOrgModal] = useState(false);
  const [newWebsiteName, setNewWebsiteName] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [orgsData, websitesData] = await Promise.all([
        request<{ organizations: Organization[] }>('/api/organizations'),
        request<{ websites: Website[] }>('/api/websites'),
      ]);
      setOrganizations(orgsData.organizations || []);
      setWebsites(websitesData.websites || []);
      if (orgsData.organizations?.length > 0 && !selectedOrgId) {
        setSelectedOrgId(orgsData.organizations[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createOrganization = async () => {
    if (!newOrgName.trim()) return;
    setIsCreating(true);
    try {
      const data = await request<{ organization: Organization }>('/api/organizations', {
        method: 'POST',
        body: { name: newOrgName },
      });
      setOrganizations((prev) => [...prev, data.organization]);
      setSelectedOrgId(data.organization.id);
      setNewOrgName('');
      setShowNewOrgModal(false);
    } catch (error) {
      console.error('Error creating organization:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const createWebsite = async () => {
    if (!newWebsiteName.trim() || !selectedOrgId) return;
    setIsCreating(true);
    try {
      const data = await request<{ website: Website }>('/api/websites', {
        method: 'POST',
        body: { name: newWebsiteName, organizationId: selectedOrgId },
      });
      setNewWebsiteName('');
      setShowNewWebsiteModal(false);
      // Navigate to the new website
      navigate(`/websites/${data.website.id}`);
    } catch (error) {
      console.error('Error creating website:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const features = [
    {
      icon: Globe,
      title: 'Websites & Funnels',
      description: 'Build beautiful websites with AI prompts',
      color: 'from-blue-500 to-cyan-500',
      available: true,
      action: () => setShowNewWebsiteModal(true),
    },
    {
      icon: Zap,
      title: 'Automations',
      description: 'Create powerful workflows with AI',
      color: 'from-yellow-500 to-orange-500',
      available: false,
    },
    {
      icon: Users,
      title: 'Contacts & CRM',
      description: 'Manage your contacts and tags',
      color: 'from-green-500 to-emerald-500',
      available: false,
    },
    {
      icon: MessageSquare,
      title: 'Conversations',
      description: 'Email, SMS & chat in one place',
      color: 'from-purple-500 to-pink-500',
      available: false,
    },
    {
      icon: BarChart3,
      title: 'Analytics',
      description: 'Insights powered by AI',
      color: 'from-red-500 to-rose-500',
      available: false,
    },
    {
      icon: Settings,
      title: 'Settings',
      description: 'Configure your workspace',
      color: 'from-gray-500 to-slate-500',
      available: false,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-primary-500/10 to-accent-500/10 blur-3xl rounded-full" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-accent-500/10 to-primary-500/10 blur-3xl rounded-full" />
      </div>

      {/* Header */}
      <header className="relative border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">SyncSuite</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/test">
                <Button variant="ghost" size="sm">
                  <CheckCircle className="w-4 h-4" />
                  System Test
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-white">{profile?.full_name || 'User'}</p>
                  <p className="text-xs text-white/60">{user?.email}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={signOut}>
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="relative max-w-7xl mx-auto px-6 py-12">
        {/* Welcome section */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-white mb-3">
            Welcome back, {profile?.full_name?.split(' ')[0] || 'there'}! 👋
          </h1>
          <p className="text-xl text-white/60">
            What would you like to build today?
          </p>
        </div>

        {/* Organization check */}
        {!isLoading && organizations.length === 0 && (
          <div className="mb-12 p-8 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl">
            <div className="flex items-center gap-4">
              <Building2 className="w-12 h-12 text-yellow-400" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white mb-1">Create your first organization</h2>
                <p className="text-white/60">
                  You need an organization to create websites and manage your projects.
                </p>
              </div>
              <Button onClick={() => setShowNewOrgModal(true)}>
                <Plus className="w-4 h-4" />
                Create Organization
              </Button>
            </div>
          </div>
        )}

        {/* Websites section */}
        {websites.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Your Websites</h2>
              <Button onClick={() => setShowNewWebsiteModal(true)} size="sm">
                <Plus className="w-4 h-4" />
                New Website
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {websites.map((website) => (
                <Link
                  key={website.id}
                  to={`/websites/${website.id}`}
                  className="group p-6 bg-white/5 rounded-xl border border-white/10 hover:border-primary-500/50 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-primary-400 transition-colors" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{website.name}</h3>
                  <p className="text-sm text-white/40">
                    {website.organizations?.name || 'Personal'}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Quick action - only show if they have an org */}
        {organizations.length > 0 && (
          <div className="mb-12 p-8 bg-gradient-to-r from-primary-500/20 to-accent-500/20 rounded-2xl border border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Start with AI</h2>
                <p className="text-white/60 max-w-xl">
                  Tell the AI what you want to create. Whether it's a landing page, email sequence, 
                  or automation — just describe it in plain English.
                </p>
              </div>
              <Button size="lg" onClick={() => setShowNewWebsiteModal(true)}>
                <Sparkles className="w-5 h-5" />
                Create Website
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              onClick={feature.available ? feature.action : undefined}
              className={`group relative p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300 ${
                feature.available ? 'cursor-pointer hover:scale-[1.02]' : 'opacity-60'
              }`}
            >
              <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.color} mb-4`}>
                <feature.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-white/60 mb-4">{feature.description}</p>
              {!feature.available && (
                <span className="inline-flex items-center px-2 py-1 bg-white/10 rounded-full text-xs text-white/60">
                  Coming soon
                </span>
              )}
            </div>
          ))}
        </div>

        {/* System status */}
        <div className="mt-12 p-6 bg-white/5 rounded-2xl border border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              <span className="text-white/60">All systems operational</span>
            </div>
            <Link to="/test" className="text-primary-400 hover:text-primary-300 text-sm">
              View system status →
            </Link>
          </div>
        </div>
      </main>

      {/* New Organization Modal */}
      {showNewOrgModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Create Organization</h2>
            <Input
              label="Organization Name"
              placeholder="My Company"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createOrganization()}
            />
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={() => setShowNewOrgModal(false)}>
                Cancel
              </Button>
              <Button onClick={createOrganization} loading={isCreating}>
                Create
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* New Website Modal */}
      {showNewWebsiteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Create New Website</h2>
            <div className="space-y-4">
              <Input
                label="Website Name"
                placeholder="My Awesome Website"
                value={newWebsiteName}
                onChange={(e) => setNewWebsiteName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createWebsite()}
              />
              {organizations.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1.5">
                    Organization
                  </label>
                  <select
                    value={selectedOrgId}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  >
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={() => setShowNewWebsiteModal(false)}>
                Cancel
              </Button>
              <Button onClick={createWebsite} loading={isCreating} disabled={!newWebsiteName.trim()}>
                <Sparkles className="w-4 h-4" />
                Create & Start Building
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
